import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import NotificationSnackbar from '../NotificationSnackbar';
import { useNotification } from '../../hooks/useNotification';
import { useTeamUpdateNotificationSettings } from '../../hooks/useTeamUpdateNotificationSettings';
import { translate } from '../../i18n';
import type { TxKeyPath } from '../../i18n';
import userStore from '../../stores/UserStore';
import { isSuperAdminUserRole } from '../../utilities/platformAdminAccess';
import { TeamUpdateManager } from '../../models/TeamUpdateManager';
import { TeamUpdateCallManager } from '../../models/TeamUpdateCallManager';
import type { ITeamUpdateCall, ITeamUpdateGroup, ITeamUpdatePost, ITeamUpdateReply } from '../../types/TeamUpdate';
import {
  ensureDesktopNotificationPermission,
  showDesktopTeamUpdateNotification,
} from '../../utilities/teamUpdateDesktopNotify';
import { decideTeamUpdateNotification } from '../../utilities/teamUpdateNotificationSettings';
import { recordTeamUpdateGroupDelivery } from '../../services/teamUpdateEmailService';
import { toDate } from '../../utilities/timestampUtils';
import { shouldNotifyLiveTeamUpdateMessage } from '../../utilities/teamUpdateLiveNotify';
import { isTeamUpdateCallLive } from '../../utilities/teamUpdateCall';

type SeenMap = Map<string, Set<string>>;

function ensureSet(map: SeenMap, groupId: string): Set<string> {
  let set = map.get(groupId);
  if (!set) {
    set = new Set();
    map.set(groupId, set);
  }
  return set;
}

function viewingGroupIdFromPath(pathname: string): string | null {
  const match = /^\/team-updates\/([^/]+)\/?$/.exec(pathname);
  return match?.[1] || null;
}

function latestCreatedAt(
  items: Array<{ createdAt?: ITeamUpdatePost['createdAt'] }>
): Timestamp | null {
  let latestMs = 0;
  items.forEach((item) => {
    const ms = toDate(item.createdAt)?.getTime() || 0;
    if (ms > latestMs) latestMs = ms;
  });
  return latestMs > 0 ? Timestamp.fromMillis(latestMs) : null;
}

function recordDeliveryForMessages(
  groupId: string,
  userId: string,
  items: Array<{ createdAt?: ITeamUpdatePost['createdAt'] }>
): void {
  const through = latestCreatedAt(items);
  if (!through) return;
  void recordTeamUpdateGroupDelivery(groupId, userId, through).catch((error) => {
    console.error('Failed to record team update delivery', { groupId, error });
  });
}

/**
 * Listens to Team update chats for the signed-in user and surfaces new posts/replies
 * from others via in-app snackbar (and desktop notification when the tab is hidden),
 * honouring mute / work-hours settings.
 */
export const TeamUpdateLiveNotifier: React.FC = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, message, severity, showNotification, closeNotification } = useNotification();
  const { settings } = useTeamUpdateNotificationSettings();
  const userId = userStore.user?.userId || '';
  const isSuperAdmin = isSuperAdminUserRole(userStore.user?.userRole);
  const [groups, setGroups] = useState<ITeamUpdateGroup[]>([]);

  const seededCallGroups = useRef<Set<string>>(new Set());
  const seenPosts = useRef<SeenMap>(new Map());
  const seenReplies = useRef<SeenMap>(new Map());
  const seenLiveCalls = useRef<Set<string>>(new Set());
  const settingsRef = useRef(settings);
  const viewingGroupIdRef = useRef<string | null>(viewingGroupIdFromPath(location.pathname));

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    viewingGroupIdRef.current = viewingGroupIdFromPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!userId) {
      setGroups([]);
      seededCallGroups.current.clear();
      seenPosts.current.clear();
      seenReplies.current.clear();
      seenLiveCalls.current.clear();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const list = isSuperAdmin
          ? await TeamUpdateManager.listAllGroups()
          : await TeamUpdateManager.listGroupsForUser(userId);
        if (!cancelled) setGroups(list);
      } catch (error) {
        console.error('Team update live notifier: failed to load groups', error);
        if (!cancelled) setGroups([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, isSuperAdmin]);

  // Ask for desktop notification permission only after login, once the user
  // is actually in Team updates (never on login / public routes).
  useEffect(() => {
    if (!userId) return;
    if (!settings.enabled || !settings.desktopEnabled) return;
    if (!location.pathname.startsWith('/team-updates')) return;
    ensureDesktopNotificationPermission();
  }, [userId, settings.enabled, settings.desktopEnabled, location.pathname]);

  const notifyNew = useCallback(
    (kind: 'post' | 'reply' | 'call', group: ITeamUpdateGroup, authorName: string, preview: string) => {
      const decision = decideTeamUpdateNotification(settingsRef.current, {
        kind: kind === 'call' ? 'post' : kind,
        groupId: group.id,
        viewingGroupId: viewingGroupIdRef.current,
        documentHidden: typeof document !== 'undefined' ? document.hidden : false,
      });
      if (!decision.allowInApp && !decision.allowDesktop) return;

      const key =
        kind === 'post'
          ? ('teamUpdates.notifications.newPost' as TxKeyPath)
          : kind === 'reply'
            ? ('teamUpdates.notifications.newReply' as TxKeyPath)
            : ('teamUpdates.notifications.newCall' as TxKeyPath);
      const snack = translate(key, {
        author: authorName || 'Someone',
        group: group.name,
      });

      if (decision.allowInApp) {
        showNotification(snack, 'info');
      }

      if (decision.allowDesktop) {
        const bodyPreview = preview.trim().slice(0, 120) || snack;
        showDesktopTeamUpdateNotification({
          title: translate('teamUpdates.notifications.desktopTitle' as TxKeyPath, {
            group: group.name,
          }),
          body:
            kind === 'call'
              ? snack
              : `${authorName || 'Someone'}: ${bodyPreview}`,
          tag: `team-update-${kind}-${group.id}`,
          onClick: () => navigate(`/team-updates/${group.id}`),
        });
      }
    },
    [navigate, showNotification]
  );

  useEffect(() => {
    if (!userId || groups.length === 0) return;

    // Anything created before this attach is treated as backlog (cache → server).
    const listenerAttachedAtMs = Date.now();

    const unsubs = groups.flatMap((group) => {
      const unsubPosts = TeamUpdateManager.observeGroupPosts(group.id, (posts: ITeamUpdatePost[]) => {
        const seen = ensureSet(seenPosts.current, group.id);
        recordDeliveryForMessages(group.id, userId, posts);
        posts.forEach((post) => {
          const shouldNotify = shouldNotifyLiveTeamUpdateMessage({
            messageId: post.id,
            authorId: post.authorId,
            currentUserId: userId,
            createdAt: post.createdAt,
            listenerAttachedAtMs,
            alreadySeenIds: seen,
          });
          seen.add(post.id);
          if (!shouldNotify) return;
          const preview = (post.title || post.body || '').trim();
          notifyNew('post', group, post.authorName, preview);
        });
      });

      const unsubReplies = TeamUpdateManager.observeGroupReplies(
        group.id,
        (replies: ITeamUpdateReply[]) => {
          const seen = ensureSet(seenReplies.current, group.id);
          recordDeliveryForMessages(group.id, userId, replies);
          replies.forEach((reply) => {
            const shouldNotify = shouldNotifyLiveTeamUpdateMessage({
              messageId: reply.id,
              authorId: reply.authorId,
              currentUserId: userId,
              createdAt: reply.createdAt,
              listenerAttachedAtMs,
              alreadySeenIds: seen,
            });
            seen.add(reply.id);
            if (!shouldNotify) return;
            notifyNew('reply', group, reply.authorName, reply.content || '');
          });
        }
      );

      const unsubCall = TeamUpdateCallManager.observeCall(group.id, (call: ITeamUpdateCall | null) => {
        const seedKey = `call:${group.id}`;
        if (!seededCallGroups.current.has(seedKey)) {
          seededCallGroups.current.add(seedKey);
          if (isTeamUpdateCallLive(call)) {
            seenLiveCalls.current.add(`${group.id}:${call.startedAt?.seconds || 0}`);
          }
          return;
        }
        if (!isTeamUpdateCallLive(call)) return;
        if (call.startedBy === userId) return;
        const callKey = `${group.id}:${call.startedAt?.seconds || 0}`;
        if (seenLiveCalls.current.has(callKey)) return;
        seenLiveCalls.current.add(callKey);
        notifyNew('call', group, call.startedByName, '');
      });

      return [unsubPosts, unsubReplies, unsubCall];
    });

    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [groups, userId, notifyNew]);

  if (!userId) return null;

  return (
    <NotificationSnackbar
      open={isOpen}
      message={message}
      severity={severity}
      onClose={closeNotification}
      autoHideDuration={7000}
    />
  );
});

export default TeamUpdateLiveNotifier;
